import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PrimaryButton } from '../../../../../../../../components/common/buttons/PrimaryButton';
import { SecondaryButton } from '../../../../../../../../components/common/buttons/SecondaryButton';
import TableCell from '../../../../../../../../components/dataTable/components/TableCell';
import useAcceptAnswer from '../../../../../../../../hooks/api/games/useAcceptAnswer';
import useRejectAnswer from '../../../../../../../../hooks/api/games/useRejectAnswer';
import { ButtonSize } from '../../../../../../../../types/ButtonSizes';

interface SingleReviewAnswerActionButtonsProps {
  answerId: number;
  maxPoints: number;
}

const SingleReviewAnswerActionButtons = ({ answerId, maxPoints }: SingleReviewAnswerActionButtonsProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const gameId = params.id as string;
  const [points, setPoints] = useState(maxPoints);

  const { acceptAnswer, isLoading: isLoadingAccept, isSuccess: isSuccessAccept } = useAcceptAnswer({ gameId });
  const { rejectAnswer, isLoading: isLoadingReject, isSuccess: isSuccessReject } = useRejectAnswer({ gameId });

  const handleRejectAnswer = () => {
    rejectAnswer({ answerId });
  };

  const handleAcceptAnswer = () => {
    acceptAnswer({ answerId, points });
  };

  const isLoading = isLoadingAccept || isLoadingReject;
  const isSuccess = isSuccessAccept || isSuccessReject;

  return (
    <div className="w-full flex flex-col gap-1.5">
      {isSuccess ? (
        <div>
          {isSuccessAccept && <TableCell>{t('singleActiveGame.answers.table.accepted')}</TableCell>}
          {isSuccessReject && <TableCell>{t('singleActiveGame.answers.table.rejected')}</TableCell>}
        </div>
      ) : (
        <>
          {maxPoints > 0 && (
            <div className="flex items-center gap-2 w-full">
              <input
                type="range"
                min={0}
                max={maxPoints}
                step={1}
                value={points}
                onChange={e => setPoints(Number(e.target.value))}
                aria-label={t('singleActiveGame.answers.table.pointsSlider')}
                className="flex-1"
              />
              <span className="text-sm font-medium min-w-[3rem] text-right">
                {points}/{maxPoints}
              </span>
            </div>
          )}
          <div className="flex gap-2.5 justify-end">
            <SecondaryButton onClick={handleRejectAnswer} size={ButtonSize.Medium} disabled={isLoading}>
              {t('singleActiveGame.answers.table.incorrect')}
            </SecondaryButton>
            <PrimaryButton
              onClick={handleAcceptAnswer}
              size={ButtonSize.Medium}
              disabled={isLoading}
              loading={isLoading}
            >
              {t('singleActiveGame.answers.table.correct')}
            </PrimaryButton>
          </div>
        </>
      )}
    </div>
  );
};

export default SingleReviewAnswerActionButtons;
